import { MouseEvent as ReactMouseEvent } from 'react';

import { useOverlay } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useTooltip } from '../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import type { Data } from '../../core/components/overlay-tooltip/types.js';
import { useTooltipReducer } from '../../core/components/overlay-tooltip/useTooltipReducer.js';
import { emptyData } from '../../core/components/overlay-tooltip/utils.js';
import { useEventErrorHandler } from '../../core/hooks/useEventErrorHandler.js';
import type { Formatter } from '../../core/types/formatter.js';
import { calculateMousePositions } from '../../core/utils/calculate-mouse-positions.js';
import type { InternalSliceData } from '../types/internal/pie-chart.js';
import { getTooltipContent } from '../utils/get-tooltip-content.js';

const buildTooltipData = (
  sliceId: string,
  data: InternalSliceData,
  handlerCatch: (callback: unknown) => number,
  formatter: Formatter,
): Data => {
  const tooltipContent = getTooltipContent(
    sliceId,
    data,
    handlerCatch,
    formatter,
  );

  return {
    metadata: {
      sliceId: data.id,
    },
    geometry: 'pie',
    series: [
      {
        name: tooltipContent.name,
        value: tooltipContent.absoluteValue,
        color: tooltipContent.color,
        relativeValue: tooltipContent.relativeValue,
        relativeValueUnit: '%',
      },
    ],
  };
};

/**
 * Hook that generates handlers for mouse interactions.
 * @param sliceId - Id of the slice.
 * @param data - Slice data for this slice.
 * @param formatter - Formatter to be used.
 * @returns Handlers to perform mouse interactions.
 */
export function useSliceHandlers(
  sliceId: string,
  data: InternalSliceData,
  formatter: Formatter,
) {
  const handlerCatch = useEventErrorHandler();
  const tooltipState = useTooltip();
  const dispatchReducer = useTooltipReducer();
  const { overlayRef } = useOverlay();

  const onPointerMoveHandler = (event: ReactMouseEvent) => {
    if (tooltipState.getState().pinned || sliceId === 'slice-thinner-slices') {
      return;
    }

    const tooltipData = buildTooltipData(
      sliceId,
      data,
      handlerCatch,
      formatter,
    );

    dispatchReducer({
      type: 'SET_POSITION',
      payload: tooltipData,
    })({
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };

  const onMouseClickHandler = (event: ReactMouseEvent) => {
    event.stopPropagation();

    if (sliceId === 'slice-thinner-slices') {
      dispatchReducer({ type: 'RESET_TOOLTIP' })();
    } else {
      const tooltipData = buildTooltipData(
        sliceId,
        data,
        handlerCatch,
        formatter,
      );

      if (tooltipState.getState().pinned) {
        dispatchReducer({
          type: 'SET_PINNED',
          payload: {
            pinned: false,
            data: emptyData(),
          },
        })();

        dispatchReducer({
          type: 'SET_POSITION',
          payload: tooltipData,
        })({
          clientX: event.clientX,
          clientY: event.clientY,
        });
      } else {
        if (!overlayRef.current) {
          return;
        }

        const position = calculateMousePositions(event, overlayRef.current);

        dispatchReducer({
          type: 'SET_PINNED',
          payload: {
            pinned: true,
            relativeX: position.relative.x,
            relativeY: position.relative.y,
            data: tooltipData,
          },
        })();
      }
    }
  };

  return {
    onPointerMoveHandler,
    onMouseClickHandler,
  };
}
